import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { StringParam, useQueryParam, useQueryParams } from 'use-query-params'

import { ContentLayout } from '@app/layouts'
import { ExtendedTable, Header, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { LntButton } from '@app/components/base'
import { Show } from '@app/components/util'
import { ProductMergeModal } from '@app/modals/ProductMergeModal'
import { ProductCardDrawer } from '@app/drawers/ProductCardDrawer'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'
import { isNullable } from '@app/utils'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { EXTERNAL_PRODUCTS_CATALOG_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { QUERY_PARAMS_KEYS } from '@app/api/constants'

import { getExternalProductsList, postExternalProductsLink } from '@app/api/routes/externalProductsCatalog'

import { getMergeProductsSelectOptions } from '@app/utils/entitiesAdapters'

import { TopFilters } from './ui'
import { getColumns, getGeneralAndDependentProducts, adaptExternalProductsCatalogTableData } from './lib'
import { topFiltersQuerySchema, tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { Key } from 'react'
import type { GetExternalProductsListParams } from '@app/api/routes/externalProductsCatalog/types'
import type { FiltersParams } from '@app/types/filter'
import type { DefaultOptionType } from 'antd/es/select'
import type { ExternalProductsCatalogDataSource } from './types'

const columns = getColumns()

const title = 'Справочник внешних товаров'

export const ExternalProductsCatalogPage: React.FC = () => {
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const [topFiltersQuery, setTopFiltersQuery] = useQueryParams(topFiltersQuerySchema)
  const [queryProductId, setQueryProductId] = useQueryParam(QUERY_PARAMS_KEYS.id, StringParam)

  const queryClient = useQueryClient()
  const notification = useNotification()

  // TODO: Отказаться от productCardDrawerToggle и открывать дровер только по наличию id в query
  // TODO: Доделать логику по обновлению в query этого id
  const productCardDrawerToggle = useToggle()
  const productMergeModalToggle = useToggle()

  const [generalProductsOptions, setGeneralProductsOptions] = useState<DefaultOptionType[]>([])

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [selectedRows, setSelectedRows] = useState<ExternalProductsCatalogDataSource[]>([])

  const productsDataParams = useMemo(
    () => ({ ...paginationParams, ...topFiltersQuery }),
    [paginationParams, topFiltersQuery]
  ) as GetExternalProductsListParams

  const { data: externalProductsCatalogTableData, isLoading: isProductsListDataLoading } = useQuery({
    queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList, productsDataParams],
    queryFn: async () => await getExternalProductsList(productsDataParams),
    select: externalProductsListData => adaptExternalProductsCatalogTableData(externalProductsListData)
  })

  const createExternalProductsLinkMutation = useMutation({
    mutationFn: postExternalProductsLink,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList] })

      notification.success({
        message: 'Объединение выполнено',
        description: 'Успешное объединение товаров'
      })

      setGeneralProductsOptions([])

      productCardDrawerToggle.setToggled()
    }
  })

  const handleSelectedRowChange = (selectedRowKeys: Key[], selectedRows: ExternalProductsCatalogDataSource[]): void => {
    setSelectedIds(selectedRowKeys.map(String))
    setSelectedRows(selectedRows)
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleFilterChange = (filters: FiltersParams): void => {
    setTopFiltersQuery(filters)
    resetCurrentPage()
  }

  const handleSubmitMergeProductsModal = async (selectedId: string): Promise<void> => {
    if (!selectedId) return

    const dependentProductIds = selectedIds.filter(id => id !== selectedId)

    await createExternalProductsLinkMutation.mutateAsync({
      generalExternalProductId: selectedId,
      dependentExternalProductIds: dependentProductIds
    })

    productMergeModalToggle.setUntoggled()
  }

  const handleClickMergeProductsButton = (): void => {
    const isMatchedWithLentaProducts = selectedRows.filter(product => product.isMatchedWithProduct)
    const confirmedProducts = selectedRows.filter(product => product.isConfirmed)
    const isChosenOnlyNotConfirmedProducts = !isMatchedWithLentaProducts.length && !confirmedProducts.length

    if (isMatchedWithLentaProducts.length === 1) {
      const data = getGeneralAndDependentProducts(selectedRows)

      return createExternalProductsLinkMutation.mutate(data)
    }

    if (isMatchedWithLentaProducts.length > 1) {
      const options = getMergeProductsSelectOptions(isMatchedWithLentaProducts)
      setGeneralProductsOptions(options)

      return productMergeModalToggle.setToggled()
    }

    if (confirmedProducts.length > 1) {
      const options = getMergeProductsSelectOptions(confirmedProducts)
      setGeneralProductsOptions(options)

      return productMergeModalToggle.setToggled()
    }

    const data = getGeneralAndDependentProducts(selectedRows)
    const generalProductId = data.generalExternalProductId

    if (isChosenOnlyNotConfirmedProducts) {
      data.autoConfirm = true
    }

    setSelectedIds([generalProductId])

    return createExternalProductsLinkMutation.mutate(data)
  }

  const onRowDoubleClick = (_: unknown, record: ExternalProductsCatalogDataSource): void => {
    setSelectedIds([record.key])
    setQueryProductId(record.key)

    productCardDrawerToggle.setToggled()
  }

  const handleOpenProductCardDrawerFromToolbar = (): void => {
    setQueryProductId(selectedIds[0])

    productCardDrawerToggle.setToggled()
  }

  const handleCloseProductCardDrawer = (): void => {
    setQueryProductId(null)

    productCardDrawerToggle.setUntoggled()
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.externalProductsCatalog, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredExternalProductsCatalogDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: externalProductsCatalogTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  useEffect(() => {
    if (isNullable(queryProductId)) return

    productCardDrawerToggle.setToggled()
  }, [])

  const toolbar = [
    <Show
      condition={ selectedIds.length === 1 }
      key='show-product'
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleOpenProductCardDrawerFromToolbar }
      >
        Просмотр
      </LntButton>
    </Show>,

    <Show
      condition={ selectedIds.length > 1 }
      key='merge-products'
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleClickMergeProductsButton }
      >
        Объединить
      </LntButton>
    </Show>,

    <Show
      condition={ selectedIds.length === 1 }
      key='export-product'
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        // TODO: Добавить блокировку кнопке (loading), после начала экспорта, когда будет готова логика
      >
        Экспорт
      </LntButton>
    </Show>,

    <Show
      key='view_all-export-show'
      condition={ selectedIds.length > 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        // TODO: Добавить блокировку кнопке (loading), после начала экспорта, когда будет готова логика
      >
        Экспорт всех
      </LntButton>
    </Show>,

    <SubheaderToolbarDivider key='divider' />,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <ContentLayout>
        <TopFilters
          initialFilters={ topFiltersQuery as FiltersParams }
          onSearch={ handleFilterChange }
        />

        <ExtendedTable
          loading={ isProductsListDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredExternalProductsCatalogDataSource }
          toolbar={ toolbar }
          onRowDoubleClick={ onRowDoubleClick }
          rowSelection={ {
            onChange: handleSelectedRowChange,
            selectedRowKeys: selectedIds
          } }
          pagination={ {
            ...pagination,
            total: externalProductsCatalogTableData?.count,
            onChange: handlePaginationChange
          } }
        />

        <ProductMergeModal
          open={ productMergeModalToggle.value }
          title='Объединение товаров'
          options={ generalProductsOptions }
          onSubmit={ handleSubmitMergeProductsModal }
          onCancel={ productMergeModalToggle.setUntoggled }
        />

        <ProductCardDrawer
          id={ selectedIds[0] ?? queryProductId }
          open={ productCardDrawerToggle.value }
          onClose={ handleCloseProductCardDrawer }
        />
      </ContentLayout>
    </>
  )
}
